import { useState, useEffect } from "react";

// ── PALETTE ────────────────────────────────────────────────────
const white  = "#FFFFFF";
const bg     = "#F2EDE8";      // beige chaud pour sections alternées
const border = "#E8E2DC";
const txt1   = "#111111";
const txt2   = "#444444";
const txt3   = "#888888";
const terra  = "#C0622A";
const sans   = "'DM Sans', system-ui, sans-serif";
const syne   = "'Outfit', system-ui, sans-serif";
const okC    = "#2A6040";
const okBg   = "#EAF3EE";
const warnC  = "#7A5C10";
const warnBg = "#F5EFDC";
const errC   = "#8A2A10";
const errBg  = "#FDF0EC";

// ── CONFIG ─────────────────────────────────────────────────────
const TALLY_AUDIT    = "9qvDqK";
const TALLY_MONITOR  = "VL1aEM";
const TALLY_MDF      = "68QGgN";
const ADMIN_EMAIL    = "contact@albanlecocq.com";
const PRICING        = { 1: 79, 2: 129, 3: 159, 4: 189, 5: 219 };
const TYPES          = ["Brasserie / Bistrot","Restaurant gastronomique","Fast casual / Snack","Pizzeria","Restaurant asiatique","Coffee shop / Café","Dark kitchen","Autre"];

const LS_LINKS = {
  audit: {
    1: "https://fnbkit.lemonsqueezy.com/checkout/buy/832c3e71-133f-46f2-92c7-e747769a23ac",
    2: "https://fnbkit.lemonsqueezy.com/checkout/buy/43624f21-48b9-48a7-ae9a-38b0a6b2a55e",
    3: "https://fnbkit.lemonsqueezy.com/checkout/buy/8ef07e3c-492e-44de-8bcd-8ffecd166987",
    4: "https://fnbkit.lemonsqueezy.com/checkout/buy/d9e8f560-0224-4214-bda4-eac3e13dc259",
    5: "https://fnbkit.lemonsqueezy.com/checkout/buy/57d620b2-30a1-41cf-804f-142ad0ea1a8e",
  },
  auditAppel: {
    1: "https://fnbkit.lemonsqueezy.com/checkout/buy/25495578-200b-44fe-a955-6af95f249e56",
    2: "https://fnbkit.lemonsqueezy.com/checkout/buy/11eb3be8-871e-4904-8a56-9c1f012f14c1",
    3: "https://fnbkit.lemonsqueezy.com/checkout/buy/bd65e6ea-df53-4537-b421-d57fab2a1af7",
    4: "https://fnbkit.lemonsqueezy.com/checkout/buy/ee84d09d-1960-4d7f-a1dc-6b7270c5fc30",
    5: "https://fnbkit.lemonsqueezy.com/checkout/buy/dcd2332c-8ac6-45e7-b43d-65ac2c7b7f9f",
  },
  monitoring: "https://fnbkit.lemonsqueezy.com/checkout/buy/8230cc14-d155-4f1b-96d7-01aa6b3fa3df",
};

// ── BENCHMARKS ─────────────────────────────────────────────────
const BENCH = {
  "Brasserie / Bistrot":      { food:31,foodW:33,labor:32,laborW:35,prime:63,primeW:65,loyer:8,loyerW:10 },
  "Restaurant gastronomique": { food:29,foodW:32,labor:36,laborW:40,prime:65,primeW:68,loyer:7,loyerW:10 },
  "Fast casual / Snack":      { food:31,foodW:34,labor:28,laborW:31,prime:58,primeW:62,loyer:8,loyerW:10 },
  "Pizzeria":                 { food:25,foodW:28,labor:30,laborW:33,prime:55,primeW:58,loyer:7,loyerW:10 },
  "Restaurant asiatique":     { food:28,foodW:32,labor:30,laborW:34,prime:58,primeW:62,loyer:8,loyerW:10 },
  "Coffee shop / Café":       { food:32,foodW:36,labor:32,laborW:35,prime:62,primeW:66,loyer:9,loyerW:12 },
  "Dark kitchen":             { food:30,foodW:34,labor:28,laborW:32,prime:57,primeW:62,loyer:6,loyerW:9  },
  "Autre":                    { food:30,foodW:33,labor:32,laborW:35,prime:62,primeW:65,loyer:8,loyerW:10 },
};

// ── HOOKS ──────────────────────────────────────────────────────
function useIsMobile() {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => { const h = () => setM(window.innerWidth < 768); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  return m;
}

// ── DIAGNOSTIC ─────────────────────────────────────────────────
function computeDiag(form) {
  const b = BENCH[form.type] || BENCH["Autre"], ca = +form.ca;
  const food  = form.achats      ? (+form.achats / ca * 100) : null;
  const labor = form.salaires    ? (+form.salaires / ca * 100) : null;
  const loyer = form.loyer       ? (+form.loyer / ca * 100) : null;
  const prime = (form.achats && form.salaires) ? ((+form.achats + +form.salaires) / ca * 100) : null;
  const ticket = form.transactions ? (ca / +form.transactions) : null;
  const problems = [];

  if (food  && food  > b.foodW)  { const i = Math.round((food -b.food) /100*ca); problems.push({ titre:"Food cost hors norme",  desc:`Votre ratio matières est à ${food.toFixed(1)}% — ${(food-b.food).toFixed(1)} pts au-dessus de la norme (${b.food}%). Sur votre CA, cet écart représente une perte directe de marge.`, impact:i }); }
  if (labor && labor > b.laborW) { const i = Math.round((labor-b.labor)/100*ca); problems.push({ titre:"Masse salariale excessive", desc:`Votre labor cost est à ${labor.toFixed(1)}% — ${(labor-b.labor).toFixed(1)} pts au-dessus de la norme (${b.labor}%). Chaque point de trop érode directement votre marge nette.`, impact:i }); }
  if (loyer && loyer > b.loyerW) { const i = Math.round((loyer-b.loyer)/100*ca); problems.push({ titre:"Loyer disproportionné",    desc:`Votre loyer représente ${loyer.toFixed(1)}% de votre CA — la norme est sous ${b.loyerW}%. Ce déséquilibre pèse sur chaque euro encaissé.`, impact:i }); }
  if (prime && prime > b.primeW && problems.length < 2) { const i = Math.round((prime-b.prime)/100*ca); problems.push({ titre:"Prime cost critique", desc:`Votre prime cost est à ${prime.toFixed(1)}% — il reste moins de ${(100-prime).toFixed(0)}% pour couvrir loyer, charges et bénéfice.`, impact:i }); }
  if (problems.length === 0) problems.push({ titre:"Potentiel sous-exploité", desc:`Vos ratios principaux semblent dans les normes. Sans analyse de votre mix produit, les fuites invisibles — souvent les plus coûteuses — restent non identifiées.`, impact: Math.round(ca*0.03) });

  problems.sort((a, b) => b.impact - a.impact);
  let score = 100;
  if (food  && food  > b.foodW)  score -= Math.min(28, Math.round((food -b.food) *3));
  if (labor && labor > b.laborW) score -= Math.min(22, Math.round((labor-b.labor)*2.5));
  if (loyer && loyer > b.loyerW) score -= Math.min(18, Math.round((loyer-b.loyer)*2));
  if (prime && prime > b.primeW) score -= Math.min(18, Math.round((prime-b.prime)*1.5));
  score = Math.max(15, Math.min(94, score));
  const impactCache = problems.slice(1).reduce((s, p) => s + p.impact, 0);
  const verdict = score >= 75 ? "Situation saine — optimisable" : score >= 55 ? "Situation préoccupante — action requise" : score >= 35 ? "Situation critique — pertes significatives" : "Situation très critique — urgence absolue";
  return { score, verdict, problems, impactCache, food, labor, loyer, prime, ticket };
}

// ── EMAIL ──────────────────────────────────────────────────────
async function sendBrevo(to, name, subject, html, contact = null) {
  try { await fetch("/api/email", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ to, name, subject, html, contact }) }); }
  catch (e) { console.log("Email:", e); }
}

// ── COMPOSANTS ─────────────────────────────────────────────────
function Logo({ size = "sm" }) {
  const big = size === "lg";
  return (
    <div style={{ display:"flex", alignItems:"center", gap: big ? 10 : 7 }}>
      <div style={{ width: big?40:28, height: big?40:28, borderRadius: big?10:7, background:terra, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <span style={{ fontFamily:syne, fontSize: big?18:13, fontWeight:800, color:white, lineHeight:1 }}>F</span>
      </div>
      <div style={{ fontFamily:syne, fontSize: big?24:16, fontWeight:800, color:txt1, letterSpacing:"-0.5px", lineHeight:1 }}>
        F&B Kit<span style={{ color:terra }}>.</span>
      </div>
    </div>
  );
}

function FInput({ label, hint, type="text", value, onChange, placeholder, prefix, required }) {
  const [f, setF] = useState(false);
  return (
    <div style={{ marginBottom:14 }}>
      {label && <label style={{ display:"block", fontSize:12, fontWeight:600, color:txt2, marginBottom: hint?3:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>{label}{required && <span style={{ color:terra }}> *</span>}</label>}
      {hint && <div style={{ fontSize:11, color:txt3, marginBottom:5, lineHeight:1.4 }}>{hint}</div>}
      <div style={{ position:"relative" }}>
        {prefix && <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:txt3, fontSize:13, pointerEvents:"none" }}>{prefix}</span>}
        <input type={type} value={value} onChange={onChange} placeholder={placeholder}
          onFocus={() => setF(true)} onBlur={() => setF(false)}
          style={{ width:"100%", background:white, border:`1.5px solid ${f ? terra : border}`, borderRadius:8, padding:`11px 13px 11px ${prefix ? "26px" : "13px"}`, color:txt1, fontSize:14, outline:"none", boxSizing:"border-box", transition:"border-color 0.15s", fontFamily:sans }} />
      </div>
    </div>
  );
}

function FSelect({ label, value, onChange, options, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && <label style={{ display:"block", fontSize:12, fontWeight:600, color:txt2, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>{label}{required && <span style={{ color:terra }}> *</span>}</label>}
      <select value={value} onChange={onChange} style={{ width:"100%", background:white, border:`1.5px solid ${value ? terra : border}`, borderRadius:8, padding:"11px 13px", color: value ? txt1 : txt3, fontSize:14, outline:"none", cursor:"pointer", fontFamily:sans }}>
        <option value="">Sélectionner...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Nav({ setScreen }) {
  const m = useIsMobile();
  return (
    <nav style={{ background:white, borderBottom:`1px solid ${border}`, padding: m ? "14px 20px" : "14px 48px", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:100 }}>
      <div onClick={() => setScreen("landing")} style={{ cursor:"pointer" }}><Logo /></div>
      <div style={{ display:"flex", alignItems:"center", gap: m ? 10 : 28 }}>
        {!m && <>
          <span onClick={() => setScreen("report")} style={{ fontSize:14, color:txt2, cursor:"pointer", fontFamily:sans }}>Exemple de rapport</span>
          <span onClick={() => setScreen("order")} style={{ fontSize:14, color:txt2, cursor:"pointer", fontFamily:sans }}>Commander</span>
        </>}
        <button onClick={() => setScreen("diagnostic")} style={{ background:terra, border:"none", borderRadius:8, padding: m ? "8px 14px" : "10px 20px", fontSize: m?12:13, fontWeight:700, color:white, cursor:"pointer", fontFamily:sans }}>
          {m ? "Diagnostic →" : "Diagnostic gratuit →"}
        </button>
      </div>
    </nav>
  );
}

// ══════════════════════════════════════════════════════
// LANDING
// ══════════════════════════════════════════════════════
function Landing({ setScreen }) {
  const m = useIsMobile();
  const [openFaq, setOpenFaq] = useState(null);

  const FAQ = [
    { q:"Le diagnostic gratuit est vraiment gratuit ?", r:"Oui, sans condition. Vous entrez 6 chiffres, vous recevez immédiatement votre score et le problème principal chiffré en euros. Aucune carte bancaire, aucun engagement." },
    { q:"Qui est derrière F&B Kit ?", r:"F&B Kit a été construit par un opérateur qui a lui-même perdu de l'argent avant de comprendre pourquoi. 10 restaurants ouverts sur 2 territoires. Ces outils viennent du terrain, pas d'un bureau." },
    { q:"Quelle différence avec mon expert-comptable ?", r:"Votre comptable vous dit ce qui s'est passé. F&B Kit vous dit pourquoi — et quoi corriger en priorité, avec l'impact chiffré en euros de chaque action." },
    { q:"Comment se passe la livraison du rapport ?", r:"Après paiement, votre rapport complet est envoyé par email sous 48 heures ouvrées avec vos 3 problèmes détaillés, l'analyse de votre mix produit et le plan d'action chiffré." },
    { q:"Et si je ne suis pas satisfait ?", r:"Remboursement intégral sous 48 heures sur simple demande à contact@albanlecocq.com, sans condition." },
  ];

  const Section = ({ children, bg: bgColor = white, style = {} }) => (
    <section style={{ background: bgColor, ...style }}>{children}</section>
  );

  const inner = { maxWidth:1000, margin:"0 auto", padding: m ? "56px 20px" : "80px 48px" };

  return (
    <div style={{ background:white, fontFamily:sans, color:txt1 }}>

      {/* HERO */}
      <Section>
        <div style={{ ...inner, paddingBottom: m ? 40 : 60 }}>
          <div style={{ maxWidth:680 }}>
            <div style={{ display:"inline-block", background: terra+"15", borderRadius:6, padding:"4px 12px", fontSize:11, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:24 }}>
              Gratuit · 2 minutes · Sans engagement
            </div>
            <h1 style={{ fontFamily:syne, fontSize: m ? 36 : 58, fontWeight:800, color:txt1, margin:"0 0 20px", lineHeight:1.05, letterSpacing:"-1.5px" }}>
              Restaurateurs : identifiez où vous perdez <span style={{ color:terra }}>jusqu'à 3 000 €/mois.</span>
            </h1>
            <p style={{ fontSize: m?15:18, color:txt2, lineHeight:1.75, margin:"0 0 32px", maxWidth:520 }}>
              Entrez 6 chiffres. Recevez immédiatement le diagnostic de vos fuites de marge — chiffré en euros, actionnable cette semaine.
            </p>
            <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
              <button onClick={() => setScreen("diagnostic")} style={{ background:terra, border:"none", borderRadius:8, padding: m?"13px 22px":"14px 30px", fontSize: m?15:16, fontWeight:700, color:white, cursor:"pointer", fontFamily:sans }}>
                Voir où passe mon argent →
              </button>
              <button onClick={() => setScreen("report")} style={{ background:"transparent", border:`1.5px solid ${border}`, borderRadius:8, padding: m?"13px 18px":"14px 24px", fontSize: m?14:15, fontWeight:500, color:txt2, cursor:"pointer", fontFamily:sans }}>
                Voir un exemple de rapport
              </button>
            </div>
            <div style={{ marginTop:14, fontSize:12, color:txt3 }}>Aucune carte bancaire · Résultat immédiat</div>
          </div>
        </div>
      </Section>

      {/* STATS */}
      <Section bg={bg}>
        <div style={{ ...inner, paddingTop:40, paddingBottom:40 }}>
          <div style={{ display:"grid", gridTemplateColumns: m ? "1fr 1fr" : "repeat(4,1fr)", gap: m ? 24 : 0 }}>
            {[
              { val:"+ 2 300 €", label:"perdus en moyenne sur le food cost seul", color:errC },
              { val:"37 %", label:"des restaurateurs ignorent leur food cost réel", color:warnC },
              { val:"10", label:"restaurants ouverts et opérés par le fondateur", color:terra },
              { val:"48h", label:"pour recevoir votre rapport d'audit complet", color:okC },
            ].map(({ val, label, color }, i) => (
              <div key={i} style={{ padding: m ? "0" : "0 32px", borderLeft: (!m && i > 0) ? `1px solid ${border}` : "none" }}>
                <div style={{ fontFamily:syne, fontSize: m?28:36, fontWeight:800, color, lineHeight:1, marginBottom:6 }}>{val}</div>
                <div style={{ fontSize:13, color:txt3, lineHeight:1.5 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* CRÉDIBILITÉ */}
      <Section>
        <div style={{ ...inner }}>
          <div style={{ display:"grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap: m ? 32 : 80, alignItems:"center" }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:16 }}>Construit par un opérateur terrain</div>
              <h2 style={{ fontFamily:syne, fontSize: m?26:38, fontWeight:800, color:txt1, margin:"0 0 20px", lineHeight:1.1, letterSpacing:"-1px" }}>
                J'ai perdu de l'argent avant de comprendre <span style={{ color:terra }}>où il partait.</span>
              </h2>
              <p style={{ fontSize: m?14:16, color:txt2, lineHeight:1.8, margin:"0 0 24px" }}>
                F&B Kit a été construit par un opérateur qui a ouvert 10 restaurants sur 2 territoires. Ces outils viennent du terrain, pas d'un bureau de conseil.
              </p>
              <blockquote style={{ borderLeft:`3px solid ${terra}`, paddingLeft:20, margin:"0", fontStyle:"italic", fontSize: m?14:15, color:txt2, lineHeight:1.7 }}>
                "J'ai ouvert 10 restaurants sur 2 territoires. J'ai perdu de l'argent avant de comprendre où il partait. Ces outils, je les ai construits pour ne plus jamais avoir cette question sans réponse."
              </blockquote>
              <div style={{ marginTop:12, fontSize:13, color:txt3, paddingLeft:23 }}>Fondateur · F&B Kit</div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
              {[
                { val:"10", label:"restaurants ouverts et opérés", sub:"Sur 2 territoires différents" },
                { val:"2", label:"territoires, contextes économiques différents", sub:"Approche terrain validée" },
                { val:"15 ans", label:"d'expérience F&B opérationnelle", sub:"Pas un cabinet — un opérateur" },
              ].map(({ val, label, sub }) => (
                <div key={label} style={{ display:"flex", gap:20, alignItems:"flex-start", padding:"20px 0", borderBottom:`1px solid ${border}` }}>
                  <div style={{ fontFamily:syne, fontSize:32, fontWeight:800, color:terra, lineHeight:1, flexShrink:0, minWidth:70 }}>{val}</div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600, color:txt1, marginBottom:3 }}>{label}</div>
                    <div style={{ fontSize:12, color:txt3 }}>{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* COMMENT ÇA MARCHE */}
      <Section bg={bg}>
        <div style={{ ...inner }}>
          <div style={{ marginBottom:48 }}>
            <div style={{ fontSize:11, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Comment ça marche</div>
            <h2 style={{ fontFamily:syne, fontSize: m?26:42, fontWeight:800, color:txt1, margin:0, letterSpacing:"-1px", lineHeight:1.05 }}>
              De vos données à vos <span style={{ color:terra }}>décisions.</span>
            </h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns: m ? "1fr" : "repeat(4,1fr)", gap: m ? 0 : 0 }}>
            {[
              { n:"01", titre:"Diagnostic gratuit", impact:"Vous voyez immédiatement combien vous perdez", desc:"6 chiffres. Score et problème principal identifié." },
              { n:"02", titre:"Problème identifié", impact:"Chiffré en euros par mois, pas en jargon", desc:"Un constat clair, un impact estimé." },
              { n:"03", titre:"Audit complet", impact:"Plan d'action concret sous 48h", desc:"Rapport détaillé, 3 actions prioritaires chiffrées." },
              { n:"04", titre:"Vous agissez", impact:"Premiers résultats dès cette semaine", desc:"Corrections concrètes, actionnables immédiatement." },
            ].map((h, i) => (
              <div key={i} style={{ padding: m ? "24px 0" : "0 28px", borderLeft: (!m && i > 0) ? `1px solid ${border}` : "none", borderBottom: (m && i < 3) ? `1px solid ${border}` : "none" }}>
                <div style={{ fontFamily:syne, fontSize:48, fontWeight:800, color:terra, opacity:0.15, lineHeight:1, marginBottom:14 }}>{h.n}</div>
                <div style={{ fontSize:15, fontWeight:700, color:txt1, marginBottom:6 }}>{h.titre}</div>
                <div style={{ fontSize:13, color:terra, fontWeight:600, marginBottom:8 }}>→ {h.impact}</div>
                <div style={{ fontSize:13, color:txt3, lineHeight:1.6 }}>{h.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* OFFRES */}
      <Section>
        <div style={{ ...inner }}>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Nos offres</div>
            <h2 style={{ fontFamily:syne, fontSize: m?26:42, fontWeight:800, color:txt1, margin:"0 0 8px", letterSpacing:"-1px", lineHeight:1.05 }}>
              Le diagnostic vous montre le problème.<br />L'audit vous donne les <span style={{ color:terra }}>solutions.</span>
            </h2>
          </div>
          <div style={{ background: terra+"10", border:`1px solid ${terra}28`, borderRadius:8, padding:"11px 18px", marginBottom:32, fontSize:13, color:terra, fontWeight:500 }}>
            Un seul problème identifié représente souvent 1 000 à 5 000 €/mois récupérables.
          </div>

          <div style={{ display:"grid", gridTemplateColumns: m ? "1fr" : "repeat(4,1fr)", gap:1, background:border, border:`1px solid ${border}`, borderRadius:12, overflow:"hidden" }}>
            {[
              {
                name:"Diagnostic", type:"Gratuit", price:"0", unit:"€", sub:null,
                badge:null, hi:false,
                inputs:["CA HT mensuel","Achats matières","Salaires bruts","Loyer mensuel","Type de restaurant","Nb de transactions"],
                outputs:["Score de santé 0–100","2 problèmes identifiés sans solution","Impact estimé en €/mois","Ticket moyen calculé"],
                teaser:"3 actions concrètes pour corriger → Audit 79€",
                cta:"Voir où passe mon argent", action: () => setScreen("diagnostic"),
              },
              {
                name:"Audit complet", type:"One-off", price:"79", unit:"€", sub:null,
                badge:"LE PLUS CHOISI", hi:true,
                inputs:["Les 6 champs du diagnostic","+ Nb couverts / jours ouverts","+ Heures travaillées","+ Ventilation CA food/boissons","+ Stocks début et fin de mois","+ Déchets estimés","+ Export caisse OU saisie manuelle"],
                outputs:["Tous les KPI du diagnostic","+ 7 KPI supplémentaires","+ Benchmarks secteur","+ Analyse mix produit","+ 3 actions prioritaires chiffrées","+ Rapport PDF sous 48h"],
                teaser:"Ces actions ont-elles fonctionné ? → Monitoring 49€/mois",
                cta:"Commander l'audit — rentabilisé en 1 décision", action: () => setScreen("order"),
              },
              {
                name:"Monitoring", type:"Abonnement", price:"49", unit:"€/mois", sub:null,
                badge:null, hi:false,
                inputs:["Les champs de l'audit","+ Total factures food HT","+ Total factures hors food","+ Valeur stock début/fin","+ Upload export POS mensuel","Saisie ou upload — au choix"],
                outputs:["Tous les KPI de l'audit","+ COGS évalué mensuel","+ Évolution M vs M-1","+ Évolution M vs M-12","+ Alertes dérive automatiques","+ Score de progression","+ Tableau de bord historique"],
                teaser:"Plusieurs restaurants ? Zéro saisie → Mon Directeur Financier",
                cta:"Démarrer le monitoring", action: () => window.open(LS_LINKS.monitoring, "_blank"),
              },
              {
                name:"Mon Directeur Financier", type:"Service sur mesure", price:"197", unit:"€/mois", sub:"497€ setup",
                badge:"PLACES LIMITÉES", hi:false,
                inputs:["Export POS complet","Factures fournisseurs (PDF/photo)","Fiches de paie","Fiches recettes (setup unique)","Zéro saisie manuelle"],
                outputs:["Tout le monitoring","+ Food cost théorique vs réel","+ Écarts portions / gaspillage","+ Menu engineering complet","+ Analyse fournisseurs","+ Rapport PDF automatique","+ Alertes WhatsApp proactives"],
                teaser:null,
                cta:"Rejoindre la liste d'attente →", action: () => window.open(`https://tally.so/r/${TALLY_MDF}`, "_blank"),
              },
            ].map((o, i) => (
              <div key={i} style={{ background:white, padding:"28px 24px", display:"flex", flexDirection:"column", position:"relative" }}>
                {o.badge && (
                  <div style={{ position:"absolute", top:-1, left:"50%", transform:"translateX(-50%)", background: o.hi ? terra : txt1, color:white, fontSize:9, fontWeight:700, letterSpacing:1.5, padding:"3px 12px", borderRadius:"0 0 8px 8px", whiteSpace:"nowrap", fontFamily:sans }}>
                    {o.badge}
                  </div>
                )}
                <div style={{ marginTop: o.badge ? 12 : 0 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>{o.name}</div>
                  <div style={{ fontSize:11, color:txt3, marginBottom:14 }}>{o.type}</div>
                  <div style={{ display:"flex", alignItems:"baseline", gap:4, marginBottom: o.sub ? 2 : 16 }}>
                    <span style={{ fontFamily:syne, fontSize:36, fontWeight:800, color:o.hi?terra:txt1, lineHeight:1 }}>{o.price}</span>
                    <span style={{ fontSize:14, color:txt3 }}>{o.unit}</span>
                  </div>
                  {o.sub && <div style={{ fontSize:11, color:txt3, marginBottom:16 }}>{o.sub}</div>}
                </div>

                <div style={{ height:1, background:border, marginBottom:16 }} />

                {/* OUTPUTS */}
                <div style={{ flex:1, marginBottom:16 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:txt3, letterSpacing:1.5, textTransform:"uppercase", marginBottom:8 }}>Ce que vous recevez</div>
                  {o.outputs.map(f => (
                    <div key={f} style={{ display:"flex", gap:7, fontSize:11, color:txt2, padding:"2px 0", lineHeight:1.4 }}>
                      <span style={{ color:okC, flexShrink:0 }}>✓</span>{f}
                    </div>
                  ))}
                </div>

                {o.teaser && (
                  <div style={{ background:bg, borderRadius:6, padding:"8px 12px", fontSize:11, color:txt3, fontStyle:"italic", marginBottom:14, lineHeight:1.5 }}>
                    {o.teaser}
                  </div>
                )}

                <button onClick={o.action} style={{ width:"100%", border: o.hi ? "none" : `1.5px solid ${border}`, borderRadius:8, padding:"12px 10px", fontSize:12, fontWeight:700, cursor:"pointer", background: o.hi ? terra : o.badge==="PLACES LIMITÉES" ? txt1 : "transparent", color: o.hi || o.badge==="PLACES LIMITÉES" ? white : txt2, fontFamily:sans, lineHeight:1.3 }}>
                  {o.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* TÉMOIGNAGES */}
      <Section bg={bg}>
        <div style={{ ...inner }}>
          <div style={{ fontSize:11, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Résultats terrain</div>
          <h2 style={{ fontFamily:syne, fontSize: m?26:38, fontWeight:800, color:txt1, margin:"0 0 40px", letterSpacing:"-1px" }}>
            Ce que des restaurateurs ont <span style={{ color:terra }}>découvert.</span>
          </h2>
          <div style={{ display:"grid", gridTemplateColumns: m ? "1fr" : "repeat(3,1fr)", gap:1, background:border, border:`1px solid ${border}`, borderRadius:12, overflow:"hidden" }}>
            {[
              { ini:"ML", nom:"Marie L.", etab:"Brasserie · Lyon", gain:"+800 €/mois", txt:"En 24h j'ai su que mon plat le plus vendu me coûtait de l'argent. J'ai corrigé le prix la semaine suivante." },
              { ini:"TG", nom:"Thomas G.", etab:"Fast casual · Paris", gain:"+1 200 €/mois", txt:"Je pensais que mon problème était la fréquentation. Le diagnostic a montré que c'était mon food cost." },
              { ini:"SB", nom:"Samir B.", etab:"Pizzeria · Marseille", gain:"+650 €/mois", txt:"J'ai compris mes chiffres pour la première fois en 8 ans de métier. Simple, direct, actionnable." },
            ].map((t, i) => (
              <div key={i} style={{ background:white, padding:"28px 24px" }}>
                <div style={{ fontFamily:syne, fontSize:28, fontWeight:800, color:okC, marginBottom:14 }}>{t.gain}</div>
                <p style={{ fontSize:14, color:txt2, lineHeight:1.7, margin:"0 0 20px", fontStyle:"italic" }}>"{t.txt}"</p>
                <div style={{ display:"flex", alignItems:"center", gap:10, paddingTop:16, borderTop:`1px solid ${border}` }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:terra+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:terra, flexShrink:0 }}>{t.ini}</div>
                  <div><div style={{ fontSize:13, fontWeight:600, color:txt1 }}>{t.nom}</div><div style={{ fontSize:11, color:txt3 }}>{t.etab}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <div style={{ ...inner, maxWidth:740, marginLeft:"auto", marginRight:"auto" }}>
          <div style={{ fontSize:11, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>FAQ</div>
          <h2 style={{ fontFamily:syne, fontSize: m?26:38, fontWeight:800, color:txt1, margin:"0 0 40px", letterSpacing:"-1px" }}>
            Questions <span style={{ color:terra }}>fréquentes.</span>
          </h2>
          {FAQ.map((f, i) => (
            <div key={i} style={{ borderTop:`1px solid ${border}`, ...(i === FAQ.length-1 ? { borderBottom:`1px solid ${border}` } : {}) }}>
              <div onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"18px 0", cursor:"pointer", gap:16 }}>
                <span style={{ fontSize: m?13:15, fontWeight:600, color:txt1, lineHeight:1.4 }}>{f.q}</span>
                <span style={{ color:terra, fontSize:22, flexShrink:0, display:"inline-block", transition:"transform 0.15s", transform: openFaq===i ? "rotate(45deg)" : "none", lineHeight:1 }}>+</span>
              </div>
              {openFaq === i && <div style={{ fontSize:14, color:txt2, lineHeight:1.8, paddingBottom:18 }}>{f.r}</div>}
            </div>
          ))}
        </div>
      </Section>

      {/* CTA FINAL */}
      <Section bg={bg}>
        <div style={{ ...inner, textAlign:"center" }}>
          <h2 style={{ fontFamily:syne, fontSize: m?30:52, fontWeight:800, color:txt1, margin:"0 0 16px", lineHeight:1.05, letterSpacing:"-1.5px" }}>
            Commencez par savoir.<br /><span style={{ color:terra }}>C'est gratuit.</span>
          </h2>
          <p style={{ fontSize: m?14:17, color:txt2, margin:"0 auto 28px", lineHeight:1.7, maxWidth:480 }}>5 chiffres. Vos fuites de marge identifiées — immédiatement et gratuitement.</p>
          <button onClick={() => setScreen("diagnostic")} style={{ background:terra, border:"none", borderRadius:8, padding: m?"13px 28px":"15px 36px", fontSize: m?15:17, fontWeight:700, color:white, cursor:"pointer", fontFamily:sans }}>
            Voir où passe mon argent →
          </button>
          <div style={{ marginTop:12, fontSize:12, color:txt3 }}>Aucune carte bancaire · Aucun engagement</div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer style={{ background:white, borderTop:`1px solid ${border}`, padding: m ? "24px 20px" : "28px 48px", display:"flex", flexDirection: m?"column":"row", justifyContent:"space-between", alignItems: m?"flex-start":"center", gap:16 }}>
        <Logo />
        <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
          {[["CGV","cgv"],["Confidentialité","confidentialite"],["Remboursement","remboursement"]].map(([l,s]) => (
            <span key={s} onClick={() => setScreen(s)} style={{ fontSize:12, color:txt3, cursor:"pointer", fontFamily:sans }}>{l}</span>
          ))}
          <a href="mailto:contact@albanlecocq.com" style={{ fontSize:12, color:txt3, textDecoration:"none" }}>Contact</a>
        </div>
        <div style={{ fontSize:12, color:txt3 }}>© F&B Kit 2026 — Alban Lecocq</div>
      </footer>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// DIAGNOSTIC
// ══════════════════════════════════════════════════════
function Diagnostic({ setScreen }) {
  const m = useIsMobile();
  const [form, setForm] = useState({ email:"", type:"", ca:"", achats:"", salaires:"", loyer:"", transactions:"" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canSubmit = form.email && form.type && form.ca && form.salaires && form.loyer;

  const ratios = form.ca && form.salaires ? {
    food:   form.achats      ? (+form.achats / +form.ca * 100).toFixed(1) : null,
    labor:  (+form.salaires  / +form.ca * 100).toFixed(1),
    prime:  form.achats      ? ((+form.achats + +form.salaires) / +form.ca * 100).toFixed(1) : null,
    ticket: form.transactions ? (+form.ca / +form.transactions).toFixed(2) : null,
  } : null;

  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    const diag = computeDiag(form);
    const main = diag.problems[0];
    setResult({ ...diag, email: form.email, type: form.type });
    await sendBrevo(form.email, form.email, "Votre diagnostic F&B Kit",
      `<div style="font-family:sans-serif;max-width:600px;margin:0 auto;padding:32px;color:#111">
        <div style="border-left:4px solid #C0622A;padding-left:20px;margin-bottom:24px">
          <div style="font-size:11px;color:#C0622A;text-transform:uppercase;letter-spacing:2px;font-weight:700;margin-bottom:8px">Votre diagnostic F&B Kit</div>
          <h1 style="font-size:28px;margin:0 0 6px;font-weight:800">Score : ${diag.score}/100</h1>
          <p style="color:#444;margin:0;font-size:15px">${diag.verdict}</p>
        </div>
        <div style="background:#FDF0EC;border-left:3px solid #C0622A;padding:18px 20px;border-radius:8px;margin-bottom:16px">
          <div style="font-size:11px;color:#C0622A;text-transform:uppercase;letter-spacing:1.5px;font-weight:700;margin-bottom:8px">⚠ Problème principal identifié</div>
          <div style="font-size:17px;font-weight:700;margin-bottom:8px">${main.titre}</div>
          <p style="color:#444;margin:0 0 10px;line-height:1.6">${main.desc}</p>
          <div style="font-weight:700;color:#8A2A10;font-size:16px">Impact estimé : ${main.impact.toLocaleString("fr-FR")} €/mois</div>
        </div>
        ${diag.impactCache > 0 ? `<div style="background:#111;border-radius:8px;padding:14px 18px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:center">
          <div style="color:#888;font-size:13px">🔒 ${diag.problems.length-1} autre${diag.problems.length>2?"s":""} problème${diag.problems.length>2?"s":""} détecté${diag.problems.length>2?"s":""}</div>
          <div style="color:#C0622A;font-weight:700;font-size:13px">+${diag.impactCache.toLocaleString("fr-FR")} €/mois estimés</div>
        </div>` : ""}
        <a href="https://fnbkit.com" style="display:block;background:#C0622A;color:white;padding:14px 24px;border-radius:8px;text-decoration:none;font-weight:700;text-align:center;font-size:15px">Voir le rapport complet — 79 € →</a>
        <p style="color:#aaa;font-size:11px;margin-top:20px;text-align:center">F&B Kit · contact@albanlecocq.com</p>
      </div>`,
      { email: form.email, type: form.type, score: diag.score, ca: form.ca, probleme: main.titre }
    );
    await sendBrevo(ADMIN_EMAIL, "Admin", `🔔 Diagnostic — ${form.type} · ${form.email}`,
      `<p style="font-family:sans-serif"><b>Email :</b> ${form.email}<br><b>Type :</b> ${form.type}<br><b>CA :</b> ${parseInt(form.ca).toLocaleString("fr-FR")} €<br><b>Score :</b> ${diag.score}/100<br><b>Problème #1 :</b> ${main.titre} — ${main.impact.toLocaleString("fr-FR")} €/mois<br><b>Impact caché :</b> ${diag.impactCache.toLocaleString("fr-FR")} €/mois</p>`
    );
    setLoading(false);
  };

  const scoreColor = result ? (result.score >= 70 ? okC : result.score >= 45 ? warnC : errC) : warnC;

  return (
    <div style={{ background:white, minHeight:"100vh", fontFamily:sans }}>
      <div style={{ maxWidth:560, margin:"0 auto", padding: m ? "24px 20px 60px" : "48px 24px 80px" }}>
        <button onClick={() => setScreen("landing")} style={{ background:"transparent", border:"none", color:txt3, fontSize:13, cursor:"pointer", fontFamily:sans, padding:"6px 0", marginBottom:8 }}>← Retour</button>
        <div style={{ padding:"16px 0 24px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>Gratuit · Sans engagement</div>
          <h1 style={{ fontFamily:syne, fontSize: m?26:34, fontWeight:800, color:txt1, margin:"0 0 8px", letterSpacing:"-1px" }}>Votre diagnostic gratuit</h1>
          <p style={{ fontSize:13, color:txt3 }}>Des estimations suffisent — pas besoin d'être précis à l'euro près.</p>
        </div>

        {!result ? (
          <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px" }}>
            <FInput label="Votre email" hint="Le diagnostic sera envoyé à cette adresse" type="email" value={form.email} onChange={e => setF("email", e.target.value)} placeholder="jean@monrestaurant.fr" required />
            <FSelect label="Type d'établissement" value={form.type} onChange={e => setF("type", e.target.value)} options={TYPES} required />
            <div style={{ height:1, background:border, margin:"14px 0" }} />
            <div style={{ fontSize:10, fontWeight:700, color:txt3, letterSpacing:2, textTransform:"uppercase", marginBottom:14 }}>Chiffres du dernier mois</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <FInput label="CA HT" type="number" value={form.ca} onChange={e => setF("ca", e.target.value)} placeholder="48000" prefix="€" required />
              <FInput label="Achats fournisseurs" type="number" value={form.achats} onChange={e => setF("achats", e.target.value)} placeholder="16000" prefix="€" />
              <FInput label="Masse salariale" type="number" value={form.salaires} onChange={e => setF("salaires", e.target.value)} placeholder="14000" prefix="€" required />
              <FInput label="Loyer + charges" type="number" value={form.loyer} onChange={e => setF("loyer", e.target.value)} placeholder="6000" prefix="€" required />
            </div>
            <FInput label="Nb de transactions / mois" hint="Optionnel — pour calculer votre ticket moyen" type="number" value={form.transactions} onChange={e => setF("transactions", e.target.value)} placeholder="1200" />

            {ratios && (
              <div style={{ background:bg, border:`1px solid ${border}`, borderRadius:8, padding:"12px 16px", marginBottom:4, display:"flex", gap:20, flexWrap:"wrap" }}>
                {[ratios.food && { l:"Food cost", v:ratios.food+"%", w:+ratios.food>33 }, { l:"Labor", v:ratios.labor+"%", w:+ratios.labor>35 }, ratios.prime && { l:"Prime", v:ratios.prime+"%", w:+ratios.prime>65 }, ratios.ticket && { l:"Ticket moyen", v:ratios.ticket+"€", w:false }].filter(Boolean).map(({ l, v, w }) => (
                  <div key={l}><div style={{ fontSize:9, color:txt3, textTransform:"uppercase", letterSpacing:1.5, marginBottom:2 }}>{l}</div><div style={{ fontFamily:syne, fontSize:18, fontWeight:800, color: w ? errC : okC }}>{v}</div></div>
                ))}
              </div>
            )}

            <button onClick={submit} disabled={!canSubmit || loading}
              style={{ width:"100%", marginTop:16, border:"none", borderRadius:9, padding:"14px", fontSize:15, fontWeight:700, cursor: canSubmit && !loading ? "pointer" : "not-allowed", background: canSubmit && !loading ? terra : "#D4C4BA", color:white, fontFamily:sans }}>
              {loading ? "Analyse en cours..." : "Voir où passe mon argent →"}
            </button>
            <div style={{ textAlign:"center", marginTop:8, fontSize:11, color:txt3 }}>Aucune carte bancaire · Résultat envoyé par email</div>
          </div>
        ) : (
          <div>
            <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:12, textAlign:"center" }}>
              <div style={{ fontSize:10, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:14 }}>Votre diagnostic</div>
              <div style={{ position:"relative", width:100, height:62, margin:"0 auto 12px" }}>
                <svg width="100" height="62" style={{ overflow:"visible" }}>
                  <path d="M 4 56 A 44 44 0 0 1 96 56" fill="none" stroke={border} strokeWidth="6" strokeLinecap="round" />
                  <path d="M 4 56 A 44 44 0 0 1 96 56" fill="none" stroke={scoreColor} strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={`${Math.PI*44}`} strokeDashoffset={`${Math.PI*44*(1-result.score/100)}`} />
                </svg>
                <div style={{ position:"absolute", bottom:0, left:"50%", transform:"translateX(-50%)", textAlign:"center", whiteSpace:"nowrap" }}>
                  <div style={{ fontFamily:syne, fontSize:26, fontWeight:800, color:scoreColor, lineHeight:1 }}>{result.score}</div>
                  <div style={{ fontSize:9, color:txt3 }}>/100</div>
                </div>
              </div>
              <div style={{ fontSize:15, fontWeight:700, color:txt1 }}>{result.verdict}</div>
              <div style={{ fontSize:11, color:okC, marginTop:6 }}>✓ Envoyé à {result.email}</div>
            </div>

            <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:12 }}>
              <div style={{ fontSize:10, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>⚠ Problème #1 identifié</div>
              <div style={{ fontFamily:syne, fontSize:18, fontWeight:800, color:txt1, marginBottom:8 }}>{result.problems[0].titre}</div>
              <p style={{ fontSize:13, color:txt2, lineHeight:1.7, margin:"0 0 12px" }}>{result.problems[0].desc}</p>
              <div style={{ background:errBg, borderRadius:8, padding:"12px 14px" }}>
                <div style={{ fontSize:10, color:errC, textTransform:"uppercase", letterSpacing:1, marginBottom:2 }}>Impact estimé</div>
                <div style={{ fontFamily:syne, fontSize:22, fontWeight:800, color:errC }}>− {result.problems[0].impact.toLocaleString("fr-FR")} €/mois</div>
              </div>
            </div>

            {result.impactCache > 0 && (
              <div style={{ background:txt1, borderRadius:12, padding:"24px", marginBottom:12 }}>
                <div style={{ fontSize:10, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>
                  🔒 {result.problems.length-1} autre{result.problems.length>2?"s":""} problème{result.problems.length>2?"s":""} détecté{result.problems.length>2?"s":""}
                </div>
                <p style={{ fontSize:14, color:"#C4B8A8", margin:"0 0 8px", lineHeight:1.65 }}>
                  Ce n'est pas votre seul problème. Les {result.problems.length-1} autre{result.problems.length>2?"s":""} représentent probablement{" "}
                  <strong style={{ color:terra }}>{result.impactCache.toLocaleString("fr-FR")} €/mois supplémentaires</strong> — non révélé{result.problems.length>2?"s":""} dans ce diagnostic gratuit.
                </p>
                <div style={{ fontSize:12, color:"#5A4A38" }}>Révélés dans le rapport d'audit complet avec le plan d'action chiffré.</div>
              </div>
            )}

            <button onClick={() => setScreen("order")} style={{ width:"100%", background:terra, border:"none", borderRadius:9, padding:"14px", fontSize:15, fontWeight:700, color:white, cursor:"pointer", fontFamily:sans, marginBottom:8 }}>
              Voir le rapport complet — 79 € →
            </button>
            <div style={{ textAlign:"center", fontSize:11, color:txt3, marginBottom:16 }}>Paiement sécurisé · Rapport sous 48h</div>
            <div style={{ textAlign:"center" }}>
              <button onClick={() => { setResult(null); setForm({ email:"", type:"", ca:"", achats:"", salaires:"", loyer:"", transactions:"" }); }} style={{ background:"transparent", border:"none", color:txt3, fontSize:13, cursor:"pointer", fontFamily:sans }}>← Nouveau diagnostic</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CHOIX DE COMMANDE
// ══════════════════════════════════════════════════════
function OrderChoice({ setScreen }) {
  const m = useIsMobile();
  return (
    <div style={{ background:white, minHeight:"100vh", fontFamily:sans }}>
      <div style={{ maxWidth:680, margin:"0 auto", padding: m ? "24px 20px 60px" : "48px 24px 80px" }}>
        <button onClick={() => setScreen("landing")} style={{ background:"transparent", border:"none", color:txt3, fontSize:13, cursor:"pointer", fontFamily:sans, padding:"6px 0", marginBottom:8 }}>← Retour</button>
        <div style={{ padding:"16px 0 32px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Commander</div>
          <h1 style={{ fontFamily:syne, fontSize: m?26:36, fontWeight:800, color:txt1, margin:0, letterSpacing:"-1px", lineHeight:1.1 }}>
            Quelle offre vous <span style={{ color:terra }}>correspond ?</span>
          </h1>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {/* AUDIT */}
          <div onClick={() => setScreen("form")} style={{ background:white, border:`1.5px solid ${terra}`, borderRadius:12, padding:"22px 24px", cursor:"pointer", transition:"all 0.15s" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:10, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>Audit complet</div>
                <div style={{ fontFamily:syne, fontSize: m?16:18, fontWeight:800, color:txt1, marginBottom:6 }}>Rapport complet sous 48h</div>
                <div style={{ fontSize:13, color:txt2, lineHeight:1.6 }}>3 problèmes détaillés et chiffrés, analyse mix produit, 3 actions prioritaires en €. One-shot, sans engagement.</div>
                <div style={{ marginTop:10, display:"flex", flexWrap:"wrap", gap:6 }}>
                  {["3 problèmes détaillés","Mix produit","3 actions chiffrées","PDF par email"].map(f => (
                    <span key={f} style={{ fontSize:11, color:okC, background:okBg, padding:"2px 8px", borderRadius:20 }}>✓ {f}</span>
                  ))}
                </div>
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <div style={{ fontFamily:syne, fontSize:28, fontWeight:800, color:terra }}>79 €</div>
                <div style={{ fontSize:11, color:txt3 }}>one-shot</div>
              </div>
            </div>
            <div style={{ marginTop:16, background:terra, borderRadius:8, padding:"10px 16px", textAlign:"center", fontSize:13, fontWeight:700, color:white }}>
              Commander l'audit →
            </div>
          </div>

          {/* MONITORING */}
          <div onClick={() => window.open(LS_LINKS.monitoring, "_blank")} style={{ background:white, border:`1.5px solid ${border}`, borderRadius:12, padding:"22px 24px", cursor:"pointer", transition:"all 0.15s" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:10, fontWeight:700, color:txt3, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>Monitoring mensuel</div>
                <div style={{ fontFamily:syne, fontSize: m?16:18, fontWeight:800, color:txt1, marginBottom:6 }}>Suivi mensuel de vos ratios</div>
                <div style={{ fontSize:13, color:txt2, lineHeight:1.6 }}>Rapport mensuel automatique, évolution M/M-1 et M/M-12, alertes dérive, score de progression. Annulable à tout moment.</div>
                <div style={{ marginTop:10, display:"flex", flexWrap:"wrap", gap:6 }}>
                  {["Évolution mensuelle","Alertes dérive","Historique","Sans engagement"].map(f => (
                    <span key={f} style={{ fontSize:11, color:okC, background:okBg, padding:"2px 8px", borderRadius:20 }}>✓ {f}</span>
                  ))}
                </div>
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <div style={{ fontFamily:syne, fontSize:28, fontWeight:800, color:txt1 }}>49 €</div>
                <div style={{ fontSize:11, color:txt3 }}>/mois</div>
              </div>
            </div>
            <div style={{ marginTop:16, background:txt1, borderRadius:8, padding:"10px 16px", textAlign:"center", fontSize:13, fontWeight:700, color:white }}>
              Démarrer le monitoring →
            </div>
          </div>

          {/* MON DIRECTEUR FINANCIER */}
          <div onClick={() => window.open(`https://tally.so/r/${TALLY_MDF}`, "_blank")} style={{ background:txt1, border:`1.5px solid ${txt1}`, borderRadius:12, padding:"22px 24px", cursor:"pointer", transition:"all 0.15s", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:16, right:16, background:terra, color:white, fontSize:9, fontWeight:700, letterSpacing:1.5, padding:"3px 10px", borderRadius:20 }}>PLACES LIMITÉES</div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:10, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>Mon Directeur Financier</div>
                <div style={{ fontFamily:syne, fontSize: m?16:18, fontWeight:800, color:white, marginBottom:6 }}>Zéro saisie — tout automatique</div>
                <div style={{ fontSize:13, color:"#8A7A68", lineHeight:1.6 }}>Vous envoyez vos fichiers bruts. Rapport PDF livré automatiquement, food cost théorique vs réel, menu engineering, alertes WhatsApp.</div>
                <div style={{ marginTop:10, display:"flex", flexWrap:"wrap", gap:6 }}>
                  {["Food cost théorique vs réel","Menu engineering","Alertes WhatsApp","Zéro saisie"].map(f => (
                    <span key={f} style={{ fontSize:11, color:terra, background:terra+"20", padding:"2px 8px", borderRadius:20 }}>✓ {f}</span>
                  ))}
                </div>
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <div style={{ fontFamily:syne, fontSize:28, fontWeight:800, color:terra }}>197 €</div>
                <div style={{ fontSize:11, color:"#8A7A68" }}>/mois</div>
                <div style={{ fontSize:10, color:"#5A4A38", marginTop:2 }}>+ 497 € setup</div>
              </div>
            </div>
            <div style={{ marginTop:16, background:terra, borderRadius:8, padding:"10px 16px", textAlign:"center", fontSize:13, fontWeight:700, color:white }}>
              Rejoindre la liste d'attente →
            </div>
          </div>
        </div>

        <div style={{ marginTop:20, textAlign:"center", fontSize:12, color:txt3 }}>
          Pas sûr ? <span onClick={() => setScreen("diagnostic")} style={{ color:terra, cursor:"pointer", fontWeight:600 }}>Commencez par le diagnostic gratuit →</span>
        </div>
      </div>
    </div>
  );
}
function OrderForm({ setScreen }) {
  const m = useIsMobile();
  const [step, setStep] = useState(0);
  const [nbSites, setNbSites] = useState(1);
  const [path, setPath] = useState(null);
  const [form, setForm] = useState({ nom:"", email:"", tel:"", type:"", ville:"", couverts:"", ca:"", salaires:"", loyer:"", achats:"", plats:"", probleme:"", appel_video:false });
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const prix = PRICING[nbSites] + (form.appel_video ? 49 : 0);
  const paypalLink = form.appel_video ? LS_LINKS.auditAppel[nbSites] : LS_LINKS.audit[nbSites];

  const inputStyle = { width:"100%", background:white, border:`1.5px solid ${border}`, borderRadius:8, padding:"11px 13px", color:txt1, fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:sans };

  if (step === 0) return (
    <div style={{ background:white, minHeight:"100vh", fontFamily:sans }}>
      <div style={{ maxWidth:580, margin:"0 auto", padding: m ? "24px 20px 60px" : "48px 24px 80px" }}>
        <button onClick={() => setScreen("landing")} style={{ background:"transparent", border:"none", color:txt3, fontSize:13, cursor:"pointer", fontFamily:sans, padding:"6px 0", marginBottom:8 }}>← Retour</button>
        <div style={{ padding:"16px 0 24px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:10 }}>Audit · Livraison sous 48h</div>
          <h1 style={{ fontFamily:syne, fontSize: m?24:32, fontWeight:800, color:txt1, margin:0, letterSpacing:"-1px" }}>Commander votre audit</h1>
        </div>

        <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:14 }}>
          <div style={{ fontSize:10, fontWeight:700, color:txt3, letterSpacing:2, textTransform:"uppercase", marginBottom:14 }}>Nombre d'établissements</div>
          <div style={{ display:"flex", border:`1px solid ${border}`, borderRadius:8, overflow:"hidden", width:"fit-content", marginBottom:18 }}>
            {[1,2,3,4,5].map(n => <div key={n} onClick={() => setNbSites(n)} style={{ padding: m?"8px 13px":"9px 20px", cursor:"pointer", fontSize:14, fontWeight:700, background: nbSites===n ? terra : white, color: nbSites===n ? white : txt3, borderRight: n<5 ? `1px solid ${border}` : "none", transition:"all 0.15s", fontFamily:sans }}>{n}</div>)}
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", background:bg, borderRadius:8, padding:"12px 14px" }}>
            <div><div style={{ fontSize:11, color:txt3, marginBottom:2 }}>Audit {nbSites > 1 ? `${nbSites} sites` : ""}</div>{nbSites > 1 && <div style={{ fontSize:11, color:terra }}>Économie de {79*nbSites-PRICING[nbSites]}€</div>}</div>
            <div style={{ fontFamily:syne, fontSize:24, fontWeight:800, color:terra }}>{PRICING[nbSites]} €</div>
          </div>
        </div>

        <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:14 }}>
          <div style={{ fontSize:10, fontWeight:700, color:txt3, letterSpacing:2, textTransform:"uppercase", marginBottom:14 }}>Vos coordonnées</div>
          <FInput label="Prénom et nom" value={form.nom} onChange={e => setF("nom", e.target.value)} placeholder="Jean Dupont" required />
          <FInput label="Email professionnel" hint="Votre rapport sera livré ici" type="email" value={form.email} onChange={e => setF("email", e.target.value)} placeholder="jean@monrestaurant.fr" required />
          <FInput label="Téléphone" hint="Optionnel" type="tel" value={form.tel} onChange={e => setF("tel", e.target.value)} placeholder="+33 6 00 00 00 00" />
          <div style={{ height:1, background:border, margin:"12px 0" }} />
          <FSelect label="Type d'établissement" value={form.type} onChange={e => setF("type", e.target.value)} options={TYPES} required />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <FInput label="Ville" value={form.ville} onChange={e => setF("ville", e.target.value)} placeholder="Lyon..." required />
            <FInput label="Couverts / jour" type="number" value={form.couverts} onChange={e => setF("couverts", e.target.value)} placeholder="80" />
          </div>
        </div>

        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:10, fontWeight:700, color:txt3, letterSpacing:2, textTransform:"uppercase", marginBottom:12 }}>Comment nous transmettre vos données ?</div>
          <div style={{ display:"grid", gridTemplateColumns: m ? "1fr" : "1fr 1fr", gap:10 }}>
            {[
              { key:"manual", icon:"✏", title:"Je rentre mes chiffres", desc:"Saisie guidée, 5 minutes." },
              { key:"tally",  icon:"↑", title:"J'envoie mes fichiers", desc:"Upload export caisse + factures." },
            ].map(o => (
              <div key={o.key} onClick={() => setPath(o.key)} style={{ background: path===o.key ? terra+"06" : white, border:`1.5px solid ${path===o.key ? terra : border}`, borderRadius:10, padding:"18px", cursor:"pointer", transition:"all 0.15s" }}>
                <div style={{ fontSize:20, color:terra, marginBottom:6 }}>{o.icon}</div>
                <div style={{ fontSize:14, fontWeight:700, color:txt1, marginBottom:3 }}>{o.title}</div>
                <div style={{ fontSize:12, color:txt3 }}>{o.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <button onClick={() => { if (form.nom && form.email && form.type && form.ville && path) setStep(1); }}
          disabled={!form.nom || !form.email || !form.type || !form.ville || !path}
          style={{ width:"100%", border:"none", borderRadius:9, padding:"14px", fontSize:14, fontWeight:700, cursor: (form.nom && form.email && form.type && form.ville && path) ? "pointer" : "not-allowed", background: (form.nom && form.email && form.type && form.ville && path) ? terra : "#D4C4BA", color:white, fontFamily:sans }}>
          Continuer →
        </button>
      </div>
    </div>
  );

  if (step === 1) return (
    <div style={{ background:white, minHeight:"100vh", fontFamily:sans }}>
      <div style={{ maxWidth:580, margin:"0 auto", padding: m ? "24px 20px 60px" : "48px 24px 80px" }}>
        <button onClick={() => setStep(0)} style={{ background:"transparent", border:"none", color:txt3, fontSize:13, cursor:"pointer", fontFamily:sans, padding:"6px 0", marginBottom:8 }}>← Retour</button>
        <div style={{ padding:"16px 0 22px" }}>
          <div style={{ fontSize:10, color:txt3, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>Étape 2 / 3</div>
          <h2 style={{ fontFamily:syne, fontSize: m?22:28, fontWeight:800, color:txt1, margin:0, letterSpacing:"-0.5px" }}>{path === "manual" ? "Vos chiffres" : "Vos documents"}</h2>
        </div>

        {path === "manual" ? (
          <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:14 }}>
            <div style={{ background:bg, borderRadius:8, padding:"10px 12px", marginBottom:16, fontSize:12, color:txt3 }}>Des estimations arrondies suffisent.</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <FInput label="CA HT mensuel" type="number" value={form.ca} onChange={e => setF("ca", e.target.value)} placeholder="48000" prefix="€" required />
              <FInput label="Achats fournisseurs" type="number" value={form.achats||""} onChange={e => setF("achats", e.target.value)} placeholder="16000" prefix="€" />
              <FInput label="Masse salariale" type="number" value={form.salaires} onChange={e => setF("salaires", e.target.value)} placeholder="14000" prefix="€" required />
              <FInput label="Loyer + charges" type="number" value={form.loyer} onChange={e => setF("loyer", e.target.value)} placeholder="6000" prefix="€" required />
            </div>
            <div style={{ marginTop:4, marginBottom:12 }}>
              <label style={{ display:"block", fontSize:12, fontWeight:600, color:txt2, marginBottom:5, textTransform:"uppercase", letterSpacing:"0.5px" }}>Vos 5 plats les plus vendus</label>
              <textarea value={form.plats} onChange={e => setF("plats", e.target.value)} placeholder={"1. Burger maison — 16€\n2. Salade César — 13€"} rows={3} style={{ ...inputStyle, resize:"vertical" }} />
            </div>
            <div>
              <label style={{ display:"block", fontSize:12, fontWeight:600, color:txt2, marginBottom:5, textTransform:"uppercase", letterSpacing:"0.5px" }}>Où pensez-vous perdre de l'argent ?</label>
              <textarea value={form.probleme} onChange={e => setF("probleme", e.target.value)} placeholder="Ex: food cost élevé, gaspillage..." rows={2} style={{ ...inputStyle, resize:"vertical" }} />
            </div>
          </div>
        ) : (
          <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:14 }}>
            <div style={{ background:bg, borderRadius:8, padding:"10px 12px", marginBottom:16, fontSize:12, color:txt3, lineHeight:1.6 }}>
              <strong style={{ color:txt1 }}>Formulaire sécurisé ci-dessous.</strong><br />Uploadez votre export de caisse et répondez aux questions.
            </div>
            <iframe src={`https://tally.so/embed/${TALLY_AUDIT}?transparentBackground=1&dynamicHeight=1&hideTitle=1`} loading="lazy" width="100%" style={{ border:"none", minHeight:"600px" }} title="Formulaire audit F&B Kit" />
          </div>
        )}

        <div onClick={() => setF("appel_video", !form.appel_video)} style={{ border:`1.5px solid ${form.appel_video ? terra : border}`, borderRadius:10, padding:"14px 16px", background: form.appel_video ? terra+"08" : white, cursor:"pointer", transition:"all 0.15s", marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
            <div style={{ width:18, height:18, borderRadius:"50%", border:`1.5px solid ${form.appel_video ? terra : border}`, background: form.appel_video ? terra : "transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:white, flexShrink:0, marginTop:2 }}>{form.appel_video ? "✓" : ""}</div>
            <div><div style={{ fontSize:13, fontWeight:600, color: form.appel_video ? terra : txt1, marginBottom:2 }}>Appel vidéo 30 min — +49 €</div><div style={{ fontSize:12, color:txt3 }}>Présentation des résultats et priorisation des actions.</div></div>
          </div>
        </div>

        <button onClick={() => setStep(2)} style={{ width:"100%", border:"none", borderRadius:9, padding:"14px", fontSize:14, fontWeight:700, cursor:"pointer", background:terra, color:white, fontFamily:sans }}>Vérifier et payer →</button>
      </div>
    </div>
  );

  return (
    <div style={{ background:white, minHeight:"100vh", fontFamily:sans }}>
      <div style={{ maxWidth:480, margin:"0 auto", padding: m ? "24px 20px 60px" : "48px 24px 80px" }}>
        <button onClick={() => setStep(1)} style={{ background:"transparent", border:"none", color:txt3, fontSize:13, cursor:"pointer", fontFamily:sans, padding:"6px 0", marginBottom:8 }}>← Retour</button>
        <div style={{ padding:"16px 0 22px" }}>
          <div style={{ fontSize:10, color:txt3, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>Étape 3 / 3</div>
          <h2 style={{ fontFamily:syne, fontSize: m?22:28, fontWeight:800, color:txt1, margin:0, letterSpacing:"-0.5px" }}>Récapitulatif et paiement</h2>
        </div>
        <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:14 }}>
          {[["Établissement", `${form.type} · ${form.ville}`], ["Contact", form.nom], ["Email", form.email], ["Sites", `${nbSites} établissement${nbSites>1?"s":""}`], ["Appel vidéo", form.appel_video ? "Oui (+49 €)" : "Non"]].map(([k, v]) => (
            <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${border}`, fontSize:13 }}>
              <span style={{ color:txt3 }}>{k}</span><span style={{ color:txt1, fontWeight:500 }}>{v}</span>
            </div>
          ))}
          <div style={{ marginTop:16, background:bg, borderRadius:8, padding:"14px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom: form.appel_video ? 8 : 0 }}>
              <span style={{ fontSize:13, color:txt3 }}>Audit {nbSites>1 ? `${nbSites} sites` : ""}</span>
              <span style={{ fontSize:14, fontWeight:600 }}>{PRICING[nbSites]} €</span>
            </div>
            {form.appel_video && <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}><span style={{ fontSize:13, color:txt3 }}>Appel vidéo</span><span style={{ fontSize:14, fontWeight:600 }}>49 €</span></div>}
            <div style={{ borderTop:`1px solid ${border}`, marginTop:10, paddingTop:10, display:"flex", justifyContent:"space-between" }}>
              <span style={{ fontSize:14, fontWeight:700 }}>Total</span>
              <span style={{ fontFamily:syne, fontSize:24, fontWeight:800, color:terra }}>{prix} €</span>
            </div>
          </div>
        </div>
        <a href={paypalLink} target="_blank" rel="noopener noreferrer" style={{ display:"block", background:txt1, borderRadius:9, padding:"15px", fontSize:15, fontWeight:700, color:white, textAlign:"center", textDecoration:"none", fontFamily:sans, marginBottom:10 }}>
          💳 Payer {prix} € par carte ou PayPal →
        </a>
        <div style={{ textAlign:"center", fontSize:11, color:txt3, lineHeight:1.7 }}>🔒 Paiement sécurisé · Rapport sous 48h<br />Remboursé si insatisfait sous 48h</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// RAPPORT EXEMPLE
// ══════════════════════════════════════════════════════
function Report({ setScreen }) {
  const m = useIsMobile();
  const [open, setOpen] = useState(null);
  const ratios = [
    { label:"Chiffre d'affaires", value:"52 400 €", ref:"—", s:"n", note:"Mois complet" },
    { label:"Food cost", value:"37,4 %", ref:"28–33 %", s:"err", note:"+5 pts hors norme" },
    { label:"Labor cost", value:"33,1 %", ref:"30–35 %", s:"ok", note:"Dans la norme" },
    { label:"Prime cost", value:"70,5 %", ref:"< 65 %", s:"err", note:"Critique" },
    { label:"Loyer / CA", value:"11,5 %", ref:"< 10 %", s:"warn", note:"Légèrement élevé" },
    { label:"Ticket moyen", value:"24,80 €", ref:"28–38 €", s:"warn", note:"Sous le potentiel" },
  ];
  const mix = [
    { nom:"Entrecôte Charolaise", ventes:312, prix:"28 €", marge:72, cat:"Star" },
    { nom:"Burger Maison", ventes:487, prix:"16 €", marge:38, cat:"Vache" },
    { nom:"Tartare de Bœuf", ventes:89, prix:"24 €", marge:68, cat:"Énigme" },
    { nom:"Salade César", ventes:134, prix:"13 €", marge:24, cat:"Poids mort" },
    { nom:"Risotto du jour", ventes:201, prix:"22 €", marge:64, cat:"Star" },
  ];
  const actions = [
    { n:1, titre:"Passer le Burger Maison de 16 € à 18 €", impact:"+974 €/mois", delai:"Cette semaine", detail:"Votre plat le plus vendu (487/mois) est votre plus grosse fuite. À 18 €, food cost repasse sous 32%. 2 € imperceptibles = 974 € récupérés chaque mois." },
    { n:2, titre:"Retirer la Salade César de la carte", impact:"+380–600 €/mois", delai:"Prochain menu", detail:"134 ventes à 13 € avec 24% de marge. Une entrée à 16–17 € = même fréquence, marge multipliée par 2,5." },
    { n:3, titre:"Standardiser les portions des 3 plats phares", impact:"+500–1 000 €/mois", delai:"Cette semaine", detail:"Sans fiche de portion, le food cost dérive de 1 à 3 pts/mois. En 2h, documentez Entrecôte, Burger et Risotto. Une fiche en cuisine = 500 à 1 000 € récupérés." },
  ];
  const pill = s => ({ n:{bg,color:txt3,l:"— Info"}, ok:{bg:okBg,color:okC,l:"✓ Normal"}, warn:{bg:warnBg,color:warnC,l:"⚠ Vigilance"}, err:{bg:errBg,color:errC,l:"↑ Hors norme"} }[s]);
  const catSt = c => ({ Star:{bg:warnBg,color:warnC}, Vache:{bg:"#E5EEF5",color:"#1A4060"}, Énigme:{bg:"#EEE5F5",color:"#401A60"}, "Poids mort":{bg:errBg,color:errC} }[c] || {bg,color:txt3});
  const ST = ({ n, label }) => (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
      <span style={{ fontFamily:syne, fontSize:11, color:terra, fontWeight:800, flexShrink:0 }}>{String(n).padStart(2,"0")}</span>
      <span style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:txt3 }}>{label}</span>
      <div style={{ flex:1, height:1, background:border }} />
    </div>
  );

  return (
    <div style={{ background:bg, minHeight:"100vh", fontFamily:sans }}>
      <div style={{ maxWidth:760, margin:"0 auto", padding: m ? "20px 16px 56px" : "32px 24px 80px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <button onClick={() => setScreen("landing")} style={{ background:"transparent", border:"none", color:txt3, fontSize:13, cursor:"pointer", fontFamily:sans }}>← Retour</button>
          <div style={{ background: terra+"15", borderRadius:6, padding:"4px 12px", fontSize:10, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase" }}>Exemple de rapport</div>
        </div>

        <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:12, position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, background:terra }} />
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16, flexWrap:"wrap" }}>
            <div style={{ flex:1, minWidth:180 }}>
              <div style={{ fontSize:10, fontWeight:700, color:terra, letterSpacing:2, textTransform:"uppercase", marginBottom:8 }}>Audit — Octobre 2025</div>
              <h1 style={{ fontFamily:syne, fontSize: m?20:26, fontWeight:800, color:txt1, margin:"0 0 4px", lineHeight:1.2 }}>Brasserie Le Marché</h1>
              <div style={{ fontSize:13, color:txt3, marginBottom:16 }}>Brasserie / Bistrot · Lyon 2ème</div>
            </div>
            <div style={{ position:"relative", width:86, height:52, flexShrink:0 }}>
              <svg width="86" height="52" style={{ overflow:"visible" }}>
                <path d="M 4 46 A 38 38 0 0 1 82 46" fill="none" stroke={border} strokeWidth="6" strokeLinecap="round" />
                <path d="M 4 46 A 38 38 0 0 1 82 46" fill="none" stroke={warnC} strokeWidth="6" strokeLinecap="round" strokeDasharray={`${Math.PI*38}`} strokeDashoffset={`${Math.PI*38*0.46}`} />
              </svg>
              <div style={{ position:"absolute", bottom:0, left:"50%", transform:"translateX(-50%)", textAlign:"center", whiteSpace:"nowrap" }}>
                <div style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:warnC, lineHeight:1 }}>54</div>
                <div style={{ fontSize:8, color:txt3 }}>/100</div>
              </div>
            </div>
          </div>
          <div style={{ background:bg, borderRadius:8, padding:"12px 16px" }}>
            <p style={{ fontSize:13, color:txt2, margin:0, lineHeight:1.7 }}>Food cost à <strong>37,4 %</strong> = perte <strong style={{ color:errC }}>2 300–4 900 €/mois</strong>. Trois actions = <strong style={{ color:okC }}>+1 570 €/mois</strong> récupérables dès ce trimestre.</p>
          </div>
        </div>

        <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:12 }}>
          <ST n={1} label="Tableau de bord" />
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize: m?11:13 }}>
              <thead><tr style={{ borderBottom:`2px solid ${border}` }}>{["Indicateur","Résultat","Norme","Statut"].map(h => <th key={h} style={{ fontSize:9, fontWeight:700, color:txt3, letterSpacing:1.5, textTransform:"uppercase", padding:"0 8px 10px", textAlign:"left" }}>{h}</th>)}</tr></thead>
              <tbody>{ratios.map((r, i) => { const p = pill(r.s); return (<tr key={r.label} style={{ borderBottom:`1px solid ${border}`, background: i%2!==0 ? bg : white }}><td style={{ padding:"10px 8px", fontWeight:500 }}>{r.label}</td><td style={{ padding:"10px 8px", fontFamily:syne, fontSize: m?13:15, fontWeight:800, color: r.s==="err"?errC:r.s==="warn"?warnC:txt1 }}>{r.value}</td><td style={{ padding:"10px 8px", color:txt3 }}>{r.ref}</td><td style={{ padding:"10px 8px" }}><span style={{ fontSize:10, fontWeight:600, color:p.color, background:p.bg, padding:"2px 8px", borderRadius:20, whiteSpace:"nowrap" }}>{p.l}</span></td></tr>); })}</tbody>
            </table>
          </div>
        </div>

        {!m && <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:12 }}>
          <ST n={2} label="Mix produit" />
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead><tr style={{ borderBottom:`2px solid ${border}` }}>{["Plat","Ventes","Prix","Marge","Profil"].map(h => <th key={h} style={{ fontSize:9, fontWeight:700, color:txt3, letterSpacing:1.5, textTransform:"uppercase", padding:"0 8px 10px", textAlign:"left" }}>{h}</th>)}</tr></thead>
            <tbody>{mix.map((p, i) => { const cs = catSt(p.cat); return (<tr key={p.nom} style={{ borderBottom:`1px solid ${border}` }}><td style={{ padding:"10px 8px", fontWeight:500 }}>{p.nom}</td><td style={{ padding:"10px 8px", fontFamily:syne, fontSize:15, fontWeight:800 }}>{p.ventes}</td><td style={{ padding:"10px 8px", color:txt2 }}>{p.prix}</td><td style={{ padding:"10px 8px" }}><div style={{ display:"flex", alignItems:"center", gap:6 }}><div style={{ width:40, height:4, borderRadius:2, background:border, overflow:"hidden" }}><div style={{ width:`${p.marge}%`, height:"100%", background: p.marge>55?okC:p.marge>35?warnC:errC }} /></div><span style={{ fontSize:11, color:txt3 }}>{p.marge}%</span></div></td><td style={{ padding:"10px 8px" }}><span style={{ fontSize:11, fontWeight:600, color:cs.color, background:cs.bg, padding:"2px 7px", borderRadius:4 }}>{p.cat}</span></td></tr>); })}</tbody>
          </table>
        </div>}

        <div style={{ background:white, border:`1px solid ${border}`, borderRadius:12, padding:"24px", marginBottom:12 }}>
          <ST n={m?2:3} label="Les 3 actions prioritaires" />
          {actions.map((a, i) => (
            <div key={i} onClick={() => setOpen(open===i ? null : i)} style={{ border:`1px solid ${open===i ? terra+"60" : border}`, borderRadius:10, overflow:"hidden", marginBottom:10, cursor:"pointer", transition:"border-color 0.15s" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px" }}>
                <div style={{ width:28, height:28, borderRadius:"50%", background: open===i ? terra : bg, border:`1px solid ${open===i ? terra : border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <span style={{ fontFamily:syne, fontSize:13, fontWeight:800, color: open===i ? white : terra }}>{a.n}</span>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:txt1, marginBottom:3 }}>{a.titre}</div>
                  <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                    <span style={{ fontSize:11, fontWeight:700, color:okC }}>↑ {a.impact}</span>
                    <span style={{ fontSize:11, color:txt3 }}>⏱ {a.delai}</span>
                  </div>
                </div>
                <span style={{ color:txt3, transition:"transform 0.15s", display:"inline-block", transform: open===i ? "rotate(180deg)" : "none" }}>↓</span>
              </div>
              {open===i && <div style={{ padding:"0 16px 14px 54px", fontSize:13, color:txt2, lineHeight:1.8, borderTop:`1px solid ${border}`, paddingTop:10 }}>{a.detail}</div>}
            </div>
          ))}
        </div>

        <div style={{ background:txt1, borderRadius:12, padding: m?"22px":"28px 32px" }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:terra, marginBottom:8 }}>Prochaine étape</div>
          <div style={{ fontFamily:syne, fontSize: m?18:22, fontWeight:800, color:"#EDE8DC", marginBottom:8, lineHeight:1.2 }}>Cet audit est une photo.<br />Le monitoring, c'est le film.</div>
          <p style={{ fontSize:13, color:"#8A7A68", margin:"0 0 18px", lineHeight:1.7 }}>Vos actions ont-elles fonctionné ? Le monitoring mensuel le vérifie automatiquement.</p>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <button onClick={() => setScreen("order")} style={{ background:terra, border:"none", borderRadius:8, padding:"11px 20px", fontSize:13, fontWeight:700, color:white, cursor:"pointer", fontFamily:sans }}>Commander mon audit — 79 €</button>
            <a href={LS_LINKS.monitoring} target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", background:white, border:"none", borderRadius:8, padding:"11px 16px", fontSize:12, fontWeight:700, color:txt1, textDecoration:"none", fontFamily:sans }}>Monitoring — 49 €/mois →</a>
          </div>
        </div>
        <div style={{ marginTop:20, textAlign:"center", fontSize:11, color:txt3 }}>F&B Kit · Rapport confidentiel · Brasserie Le Marché · 12 novembre 2025</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// PAGES LÉGALES
// ══════════════════════════════════════════════════════
function LegalPage({ setScreen, title, children }) {
  const m = useIsMobile();
  return (
    <div style={{ background:white, minHeight:"100vh", fontFamily:sans }}>
      <div style={{ maxWidth:720, margin:"0 auto", padding: m ? "24px 20px 60px" : "48px 32px 80px" }}>
        <button onClick={() => setScreen("landing")} style={{ background:"transparent", border:"none", color:txt3, fontSize:13, cursor:"pointer", fontFamily:sans, padding:"6px 0", marginBottom:24 }}>← Retour</button>
        <h1 style={{ fontFamily:syne, fontSize: m?26:36, fontWeight:800, color:txt1, marginBottom:8, letterSpacing:"-1px" }}>{title}</h1>
        <div style={{ fontSize:11, color:txt3, marginBottom:36 }}>Dernière mise à jour : avril 2026 — Alban Lecocq — BRN I1118002771</div>
        <div style={{ fontSize:14, color:txt2, lineHeight:1.9 }}>{children}</div>
      </div>
    </div>
  );
}

function CGV({ setScreen }) {
  return (
    <LegalPage setScreen={setScreen} title="Conditions Générales de Vente">
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Article 1 — Objet</h2>
      <p>Les présentes CGV régissent les relations contractuelles entre Alban Lecocq (F&B Kit — BRN I1118002771) et tout acheteur passant commande via fnbkit.com.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Article 2 — Services proposés</h2>
      <p>F&B Kit propose des services d'audit financier personnalisés pour les restaurateurs : diagnostic gratuit, audit de rentabilité (79€), monitoring mensuel (49€/mois), et service Mon Directeur Financier (497€ setup + 197€/mois). Chaque rapport est produit sur mesure à partir des données fournies par le client.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Article 3 — Commande</h2>
      <p>La commande est validée à réception du paiement. Le client reçoit une confirmation par email. F&B Kit se réserve le droit de refuser toute commande pour des raisons légitimes.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Article 4 — Prix</h2>
      <p>Les prix sont indiqués en euros (€). F&B Kit se réserve le droit de modifier ses tarifs, sans que cela affecte les commandes déjà validées.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Article 5 — Livraison</h2>
      <p>Les rapports d'audit sont livrés par email sous <strong>48 heures ouvrées</strong> à compter de la réception du paiement et des données complètes du client.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Article 6 — Remboursement</h2>
      <p>Le client bénéficie d'une <strong>garantie de satisfaction de 48 heures</strong> à compter de la réception du rapport. Remboursement intégral sans justification sur simple demande à contact@albanlecocq.com. L'abonnement monitoring est annulable à tout moment, sans frais, avec effet à la fin de la période en cours.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Article 7 — Confidentialité</h2>
      <p>Les données financières transmises sont utilisées exclusivement pour la production du rapport. Elles ne sont jamais partagées ni revendues.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Article 8 — Responsabilité</h2>
      <p>Les rapports constituent des analyses et recommandations. Ils n'ont pas valeur de conseil comptable ou juridique certifié. La responsabilité de F&B Kit est limitée au montant payé par le client.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Article 9 — Droit applicable</h2>
      <p>Les présentes CGV sont soumises au droit mauricien.</p>
    </LegalPage>
  );
}

function Confidentialite({ setScreen }) {
  return (
    <LegalPage setScreen={setScreen} title="Politique de confidentialité">
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>1. Données collectées</h2>
      <p>F&B Kit collecte : nom, email, téléphone (optionnel), données financières (CA, achats, salaires, loyer) pour produire votre rapport, et données techniques (logs serveur).</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>2. Finalité</h2>
      <p>Les données sont utilisées pour produire votre rapport, vous envoyer votre analyse par email, et améliorer nos services.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>3. Conservation</h2>
      <p>Les données financières sont conservées pour permettre le suivi mensuel (monitoring). Vous pouvez demander leur suppression à tout moment.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>4. Partage</h2>
      <p>Vos données ne sont jamais vendues ni partagées avec des tiers à des fins commerciales.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>5. Vos droits</h2>
      <p>Vous disposez des droits d'accès, rectification, suppression et opposition. Pour les exercer : contact@albanlecocq.com</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>6. Cookies</h2>
      <p>Le site utilise uniquement des cookies techniques nécessaires au fonctionnement. Aucun cookie publicitaire ou de tracking tiers.</p>
    </LegalPage>
  );
}

function Remboursement({ setScreen }) {
  return (
    <LegalPage setScreen={setScreen} title="Politique de remboursement">
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Notre garantie</h2>
      <p>F&B Kit offre une <strong>garantie de satisfaction de 48 heures</strong> à compter de la réception du rapport, sans condition ni justification.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Comment demander un remboursement</h2>
      <p>Envoyez un email à <strong>contact@albanlecocq.com</strong> dans les 48 heures suivant la réception de votre rapport avec votre nom et email de commande. Aucune justification n'est nécessaire. Remboursement traité sous 48 heures.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Abonnement Monitoring</h2>
      <p>Annulable à tout moment, sans frais. L'annulation prend effet à la fin de la période de facturation en cours.</p>
      <h2 style={{ fontFamily:syne, fontSize:20, fontWeight:800, color:txt1, margin:"28px 0 10px" }}>Exceptions</h2>
      <p>Le diagnostic gratuit ne donne pas lieu à remboursement (service gratuit).</p>
    </LegalPage>
  );
}

// ══════════════════════════════════════════════════════
// ROOT
// ══════════════════════════════════════════════════════
export default function App() {
  const [screen, setScreen] = useState("landing");
  return (
    <div>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <Nav setScreen={setScreen} />
      {screen === "landing"         && <Landing         setScreen={setScreen} />}
      {screen === "diagnostic"      && <Diagnostic      setScreen={setScreen} />}
      {screen === "order"           && <OrderChoice     setScreen={setScreen} />}
      {screen === "form"            && <OrderForm       setScreen={setScreen} />}
      {screen === "report"          && <Report          setScreen={setScreen} />}
      {screen === "cgv"             && <CGV             setScreen={setScreen} />}
      {screen === "confidentialite" && <Confidentialite setScreen={setScreen} />}
      {screen === "remboursement"   && <Remboursement   setScreen={setScreen} />}
    </div>
  );
}
